import { motion } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';

import { NOPING_GREEN } from '../constants';
import { Reveal } from '../ui-helpers';

function rand(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function randFloat(min: number, max: number, decimals: number) {
  return parseFloat((Math.random() * (max - min) + min).toFixed(decimals));
}

const RED = '#FF3B3B';

function PulsingDot({ color }: { color: string }) {
  return (
    <span className='relative flex size-2.5'>
      <span className='absolute inline-flex size-full animate-ping rounded-full opacity-75' style={{ backgroundColor: color }} />
      <span className='relative inline-flex size-2.5 rounded-full' style={{ backgroundColor: color }} />
    </span>
  );
}

function AnimatedValue({ value, unit, color }: { value: string; unit: string; color: string }) {
  return (
    <motion.span key={value} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} className='font-mono text-xl font-bold tabular-nums' style={{ color }}>
      {value}
      <span className='ms-0.5 text-sm font-normal opacity-70'>{unit}</span>
    </motion.span>
  );
}

function StatCard({ label, value, unit, accentColor }: { label: string; value: string; unit: string; accentColor: string }) {
  return (
    <div className='flex items-center justify-between rounded-lg px-4 py-3' style={{ backgroundColor: `${accentColor}08`, border: `1px solid ${accentColor}25` }}>
      <span className='text-xs font-medium text-white/60'>{label}</span>
      <AnimatedValue value={value} unit={unit} color={accentColor} />
    </div>
  );
}

export function MulticonnectionBeforeAfter() {
  const { t } = useTranslation(['components/trial/lp/v1b']);

  const [pingBefore, setPingBefore] = useState(180);
  const [lossBefore, setLossBefore] = useState(12);
  const [jitterBefore, setJitterBefore] = useState(45);

  const [pingAfter, setPingAfter] = useState(3);
  const [jitterAfter, setJitterAfter] = useState(1.2);

  useEffect(() => {
    const t1 = setInterval(() => setPingBefore(rand(120, 280)), 800);
    const t2 = setInterval(() => setLossBefore(rand(2, 30)), 900);
    const t3 = setInterval(() => setJitterBefore(rand(20, 80)), 850);
    const t4 = setInterval(() => setPingAfter(rand(1, 5)), 1200);
    const t5 = setInterval(() => setJitterAfter(randFloat(0.5, 2, 1)), 1200);

    return () => {
      clearInterval(t1);
      clearInterval(t2);
      clearInterval(t3);
      clearInterval(t4);
      clearInterval(t5);
    };
  }, []);

  return (
    <div className='mt-10 grid gap-4 md:grid-cols-2'>
      <Reveal delay={0.1}>
        <div className='space-y-3 rounded-xl p-5' style={{ backgroundColor: `${RED}06`, border: `1px solid ${RED}20` }}>
          <div className='mb-4 flex items-center gap-2'>
            <PulsingDot color={RED} />
            <span className='text-sm font-semibold text-white/80'>{t('multiconnection_ba.before_label')}</span>
          </div>
          <div className='space-y-2'>
            <StatCard label='Ping' value={String(pingBefore)} unit='ms' accentColor={RED} />
            <StatCard label='Packet Loss' value={String(lossBefore)} unit='%' accentColor={RED} />
            <StatCard label='Jitter' value={String(jitterBefore)} unit='ms' accentColor={RED} />
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.25}>
        <div className='space-y-3 rounded-xl p-5' style={{ backgroundColor: `${NOPING_GREEN}06`, border: `1px solid ${NOPING_GREEN}20` }}>
          <div className='mb-4 flex items-center gap-2'>
            <PulsingDot color={NOPING_GREEN} />
            <span className='text-sm font-semibold text-white/80'>{t('multiconnection_ba.after_label')}</span>
          </div>
          <div className='space-y-2'>
            <StatCard label='Ping' value={String(pingAfter)} unit='ms' accentColor={NOPING_GREEN} />
            <StatCard label='Packet Loss' value='0' unit='%' accentColor={NOPING_GREEN} />
            <StatCard label='Jitter' value={String(jitterAfter)} unit='ms' accentColor={NOPING_GREEN} />
          </div>
        </div>
      </Reveal>
    </div>
  );
}
